// Экран 10 (веб W08) · Тарифы и оплата. SMY-75
//
// Четыре тарифа в ряд, сравнение по одинаковым строкам-единицам. Числа
// лимитов — из справочника, а не из вёрстки: их пересчитают после беты по
// фактической себестоимости (SMY-89), и застывшая таблица пережила бы
// пересчёт, не изменившись. Тарифы тоже приезжают списком: «Старт» появился
// 06.09.2026 и не потребовал здесь ни строчки.
//
// ТАРИФ БЫВАЕТ ВЫДАННЫМ, а не только оплаченным (SMY-61): админка раздаёт
// любой бесплатно на время беты. У выданного нет ни даты списания, ни кнопки
// отмены подписки, и показывать их пустыми нельзя — это намёк на списание,
// которого не будет.

import { useEffect, useState } from 'react'
import {
  MVP_UNIT_IDS, TARIFF_RANK, TARIFFS, UNIT_LABELS, UNIT_PLURALS, pluralize, type TariffId, type UnitId,
} from '@smyslika/core'
import type { PackOfferView, PaymentMethod, PaymentRow, Subscription } from '@smyslika/api-client'

import { dayLabel, rub } from '@smyslika/core'
import { usePurchase } from '../purchase.tsx'
import { useSession } from '../session.tsx'
import { Shell } from '../shell/Shell.tsx'
import { Caps, Card, StatusBadge, type ResultStatus } from '../ui/index.ts'

/** Порядок строк сравнения — тот же у всех трёх тарифов. */
const ROWS: { unit: UnitId; title: string }[] = MVP_UNIT_IDS.map((unit) => ({
  unit,
  title: UNIT_PLURALS[unit].charAt(0).toUpperCase() + UNIT_PLURALS[unit].slice(1),
}))

/**
 * Как назвать статус оплаты человеку и каким цветом.
 *
 * «В обработке» вместо «в ожидании»: счёт, по которому человек уже заплатил,
 * а мы ещё не узнали, для него не «ожидает оплаты» — он её сделал. Слово
 * должно описывать НАШЕ состояние, а не приписывать бездействие ему.
 *
 * `refunded` ставит сервер, а не экран: в базе возвращённый платёж остаётся
 * `paid`, и переводит его ручка истории (`refundOf` в routes/payments.ts).
 * Сравнивать суммы здесь нельзя. На сервере «вернули всё» решают уже два
 * места — ручка истории и долг по чеку (`owed` в admin/receipts.ts), и их
 * сверяет проверка в routes/payments.test.ts; третье, на экране, не сверял бы
 * никто, и однажды оно разошлось бы с обоими.
 */
const PAYMENT_STATUS: Record<PaymentRow['status'], { label: string; badge: ResultStatus }> = {
  pending: { label: 'В обработке', badge: 'none' },
  paid: { label: 'Оплачено', badge: 'ok' },
  failed: { label: 'Не прошёл', badge: 'danger' },
  refunded: { label: 'Возвращено', badge: 'disputed' },
}

/**
 * За что счёт. Пакет называет число, подписка — тариф. Число — с падежом
 * («+50 вопросов»): до 24.09.2026 здесь стоял именительный `UNIT_PLURALS`, и
 * история писала «+50 вопросы». Та же строка — в витрине Android
 * (`apps/mobile/src/checkout.android.ts`); правишь одну — правь и другую.
 */
function paymentTitle(row: PaymentRow): string {
  if (row.kind === 'pack') {
    return row.packAmount != null && row.unit != null
      ? `Пакет: +${row.packAmount} ${pluralize(row.packAmount, UNIT_LABELS[row.unit])}`
      : 'Пакет'
  }
  const tariff = row.tariff != null ? TARIFFS[row.tariff].label : ''
  return row.kind === 'renewal' ? `Продление тарифа «${tariff}»` : `Тариф «${tariff}»`
}

export function Tariff() {
  const { billing, catalog, me, reference, tariff, balances, refreshBalances } = useSession()

  const [subs, setSubs] = useState<Subscription[]>([])
  const [methods, setMethods] = useState<PaymentMethod[]>([])
  const [offers, setOffers] = useState<PackOfferView[]>([])
  const [history, setHistory] = useState<PaymentRow[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  // Оплата — одним путём с экраном лимитов, у гостя через аккаунт по почте
  // (SMY-341, purchase.tsx). Остатки перечитываются после каждой попытки.
  const purchase = usePurchase(() => {
    void refreshBalances()
    void catalog.balances().catch(() => {})
  })

  useEffect(() => {
    let alive = true
    void billing
      .subscription()
      .then((s) => {
        if (!alive) return
        setSubs(s.subscriptions)
        setMethods(s.methods)
      })
      .catch(() => {
        /* подписки нет — это норма, а не ошибка экрана */
      })
    void billing.packs().then((p) => alive && setOffers(p.offers)).catch(() => {})
    // История платежей — не украшение экрана: человек, у которого списались
    // деньги, ищет здесь подтверждение, что счёт вообще дошёл. Пустой
    // список и несостоявшаяся загрузка различаются: null значит «ещё едет».
    void billing.payments().then((r) => alive && setHistory(r.payments)).catch(() => alive && setHistory([]))
    return () => {
      alive = false
    }
    // Человек сменился — гость вошёл в существующий аккаунт из окна перед
    // оплатой (SMY-341): подписки, карты и история у того аккаунта свои.
  }, [billing, me?.userId])

  // Действующая подписка — та, что ещё продлевается. Отменённая остаётся в
  // списке: доступ по ней сохраняется до конца оплаченного периода.
  const active = subs.find((s) => s.autoRenew) ?? subs[0]
  // Продлевается у человека одна подписка: оплата другой отключает прежнюю
  // (`start` в billing/subscription.ts, 11.09.2026). Сказать это надо ДО
  // оплаты — иначе человек решит, что будет платить за оба тарифа.
  const renewing = subs.find((s) => s.autoRenew)
  // Тариф без подписки — выданный админкой. Ни срока списания, ни отмены.
  const granted = balances?.source === 'grant'


  return (
    <Shell account>
      <div className="content account">
        <h1 className="account__title">Тарифы и оплата</h1>
        <p className="account__lead">
          Единицы считаются за готовый результат, а не за сообщения. Лимиты тарифа обнуляются в день
          продления и не переносятся; докупленные пакеты не сгорают и копятся.
        </p>

        <div className="tariffs">
          {(reference?.tariffs ?? []).map((t) => {
            const current = t.id === tariff
            // Тариф НИЖЕ текущего не продаётся. Действует лучшее из прав
            // (`effectiveTariff` в ядре), и покупка «Плюса» человеком на
            // «Про» не изменила бы ничего — то есть взяла бы 499 ₽ за
            // ничто. Найдено на живом проде: кнопка предлагалась.
            const lower = tariff != null && TARIFF_RANK[t.id] < TARIFF_RANK[tariff as TariffId]
            return (
              <Card key={t.id} className={`tariffs__card${current ? ' tariffs__card--current' : ''}`}>
                {current ? <span className="tariffs__badge">Текущий тариф</span> : null}
                <span className="tariffs__name">{t.label}</span>
                <span className={`tariffs__price${current ? ' tariffs__price--current' : ''}`}>
                  {t.price === 0 ? '0 ₽' : `${rub(t.price)} / мес`}
                </span>

                <hr className="divider" />

                <div className="tariffs__rows">
                  {ROWS.map((r) => (
                    <div key={r.unit} className="tariffs__row">
                      <span>{r.title}</span>
                      {/* «нет» вместо нуля: ноль читается как «кончились», а
                          категория в этот тариф просто не входит. */}
                      <span className={t.limits[r.unit] > 0 ? 'tariffs__value' : 'tariffs__value tariffs__value--none'}>
                        {t.limits[r.unit] > 0 ? t.limits[r.unit] : 'нет'}
                      </span>
                    </div>
                  ))}
                </div>

                <span style={{ flex: 1 }} />

                {current ? (
                  <>
                    <p className="tariffs__note">
                      {granted
                        ? 'Тариф выдан на время беты — платить не нужно.'
                        : // Срок — только у подписки, которая продлевается. После «Отменить»
                          // экран меняет одно `autoRenew`, прежний срок в строке остаётся, и
                          // до перезагрузки карточка писала «Продлится …» у отменённой.
                          active?.autoRenew === true && active.nextChargeAt != null
                          ? `Продлится ${dayLabel(active.nextChargeAt)}.`
                          : active?.cancelLabel != null && active.tariff === t.id
                            ? // Кто выключил продление: сам, отказом от карты или поддержка
                              // по обращению (с 11.09.2026). Без подписи выключенное не
                              // нажимавшим кнопку выглядело бы поломкой.
                              `${active.cancelLabel}.`
                            : t.id === 'free'
                              ? 'Карта не нужна. Подходит, чтобы попробовать.'
                              : ''}
                    </p>
                    {/* Кнопки управления подпиской нет у выданного тарифа и у
                        бесплатного: отменять там нечего, а кнопка-заглушка
                        обещает действие, которого не будет. */}
                    {!granted && active != null ? (
                      <button
                        type="button"
                        className="btn btn--ghost"
                        disabled={busy}
                        onClick={() => {
                          setBusy(true)
                          billing
                            .cancelRenewal(active.id)
                            .then((r) => {
                              setNote(r.message)
                              setSubs((list) =>
                                list.map((s) => (s.id === active.id ? { ...s, autoRenew: false } : s)),
                              )
                            })
                            .catch((e: Error) => setNote(e.message))
                            .finally(() => setBusy(false))
                        }}
                      >
                        {active.autoRenew ? 'Отменить автопродление' : 'Автопродление отключено'}
                      </button>
                    ) : null}
                  </>
                ) : lower ? (
                  // Не кнопка и не отговорка: понижение происходит само,
                  // когда кончается право, — покупать для этого нечего.
                  <p className="tariffs__note">
                    Ниже текущего тарифа. Перейти на него можно, отменив продление: доступ и остатки
                    останутся до конца оплаченного периода.
                  </p>
                ) : t.price > 0 ? (
                  <>
                    {/* Про ежемесячное списание сказано ДО оплаты, а не после.
                        Карта сохраняется у эквайринга безусловно — выбора на
                        форме мы не даём, — и человек узнавал об этом уже
                        постфактум, из строки «Продлится 6 октября». Это ровно
                        тот случай, когда умолчание работает против человека:
                        подписку он оформил, не прочитав, что она подписка. */}
                    <p className="tariffs__note">
                      {rub(t.price)} спишутся сразу, дальше — каждый месяц с сохранённой карты.
                      {renewing != null && renewing.tariff !== t.id
                        ? ` Подписка «${TARIFFS[renewing.tariff].label}» после оплаты продлеваться не будет: за два тарифа сразу не списываем.`
                        : ''}{' '}
                      Отменить можно в любой момент.
                    </p>
                    <button
                      type="button"
                      className="btn btn--primary"
                      disabled={purchase.busy}
                      onClick={() => void purchase.pay({ kind: 'subscription', tariff: t.id as TariffId })}
                    >
                      Перейти на {t.label}
                    </button>
                  </>
                ) : (
                  <p className="tariffs__note">Карта не нужна. Подходит, чтобы попробовать.</p>
                )}
              </Card>
            )
          })}
        </div>

        {/* Пакеты показываются ВСЕ и на любом тарифе, включая бесплатный:
            ручка докупки тариф не спрашивает, и экран, показывающий один
            пакет из трёх, врал бы про доступное. Раньше здесь стояла строка
            вопросов — остальные два докупить было негде. */}
        {offers.length > 0 ? (
          <>
            <Caps>Докупить пакет</Caps>
            <div className="packs">
              {offers.map((pack) => (
                <div key={pack.unit} className="pack">
                  <span className="pack__body">
                    {/* Родительный падеж: «Пакет вопросов», а не «Пакет
                        вопросы». UNIT_PLURALS даёт именительный — он для
                        подписи к чекбоксу «больше не спрашивать про
                        документы», и здесь не годится. */}
                    <span className="pack__title">
                      Пакет {UNIT_LABELS[pack.unit].many} +{pack.amount}
                    </span>
                    <span className="pack__note">{pack.note}</span>
                  </span>
                  <span className="pack__price">{rub(pack.price)}</span>
                  <button
                    type="button"
                    className="btn btn--accent"
                    disabled={purchase.busy}
                    onClick={() => void purchase.pay({ kind: 'pack', unit: pack.unit })}
                  >
                    Докупить
                  </button>
                </div>
              ))}
            </div>
            <p className="account__fine">
              Докупить можно в любой момент, в том числе из чата, и на любом тарифе. Купленное не
              сгорает: остаток пакета копится и переходит на следующий месяц.
            </p>
          </>
        ) : null}

        {methods.map((m) => (
          <div key={m.id} className="method">
            <span className="method__title">{m.title}</span>
            <span style={{ flex: 1 }} />
            {/* Отказ от сохранённых реквизитов принимается электронно —
                требование, действующее с 01.03.2026. Прятать его глубже, чем
                оформление, нельзя. */}
            <button
              type="button"
              className="method__revoke"
              disabled={busy}
              onClick={() => {
                setBusy(true)
                billing
                  .revokeMethod(m.id)
                  .then((r) => {
                    setNote(r.message)
                    setMethods((list) => list.filter((x) => x.id !== m.id))
                  })
                  .catch((e: Error) => setNote(e.message))
                  .finally(() => setBusy(false))
              }}
            >
              Отвязать карту
            </button>
          </div>
        ))}

        {(purchase.note ?? note) != null ? (
          <p className="account__note" role="status">
            {purchase.note ?? note}
          </p>
        ) : null}

        {/* Истории оплат в приложении для iOS нет, и правка здесь не забытая
            вторая половина. Цены и суммы в рублях на экране телефона запрещены
            правилами витрины App Store (SMY-134, «Экран 10 без витрины» в
            apps/mobile/README.md); там вместо истории журнал списаний,
            apps/mobile/src/screens/Limits.tsx.
            Переносящего этот блок в приложение остановит шаг verify «В
            приложении для iOS нет торговли»: с 11.09.2026 он ловит не только
            знак «₽» и слово «рубль», но и имя форматтера `rub`, `Intl` с
            валютой и денежные поля `price` и `…Rub`
            (scripts/check-storefront.mjs). Смотрит сторож только
            apps/mobile/src: здесь, в вебе, цена на экране законна. */}
        <Caps>История оплат</Caps>
        <div className="paylog">
          {history == null ? (
            <p className="muted">История ещё едет.</p>
          ) : history.length === 0 ? (
            <p className="muted">Оплат пока не было.</p>
          ) : (
            history.map((row) => (
              <div key={row.invoiceId} className="paylog__row">
                <span className="paylog__what">
                  <span className="paylog__title">{paymentTitle(row)}</span>
                  <span className="paylog__when">
                    {/* Дата ОПЛАТЫ, если она была, иначе дата счёта: у
                        неоплаченного счёта первой даты не существует. */}
                    {dayLabel(row.paidAt ?? row.createdAt)} · счёт №{row.invoiceId}
                    {/* Возврат — суммой, и при полном тоже: значок говорит, что
                        вернули всё, сумма — сколько это в деньгах. При
                        частичном значок остаётся «Оплачено», и без суммы
                        возврата не видно вовсе. */}
                    {row.refundedRub != null ? ` · возвращено ${rub(row.refundedRub)}` : ''}
                    {row.failureReason != null ? ` · ${row.failureReason}` : ''}
                  </span>
                </span>
                <span className="paylog__sum">{rub(row.amountRub)}</span>
                <StatusBadge status={PAYMENT_STATUS[row.status].badge}>
                  {PAYMENT_STATUS[row.status].label}
                </StatusBadge>
              </div>
            ))
          )}
        </div>

        <p className="account__fine">
          Отменить подписку можно в любой момент — доступ и остатки сохранятся до конца оплаченного периода.
          Возврат за неиспользованные единицы не предусмотрен. Карту хранит эквайринг: её номер к нам не
          попадает, а отвязать её можно здесь же — списания прекратятся.
        </p>
      </div>

      {/* Окно «нужен аккаунт» для гостя, нажавшего оплату (SMY-341). Сюда же
          ЮKassa возвращает человека с формы оплаты; спрашивать, куда прислать
          чек, больше не нужно — у платящего почта или номер есть всегда. */}
      {purchase.sheet}
    </Shell>
  )
}
